import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatExamDate, historyToString, calculateVolume, formatListItems, buildCombinedReason, getFacilityDisplayName } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfSectionHeader, PdfLabelValue, PdfMeasurementBox, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';

export function AortaPDF({ data, facilityInfo }) {
  const parts = [];
  if (data.reason && String(data.reason).trim()) parts.push(String(data.reason).trim());
  const histStr = historyToString(data.history);
  if (histStr) parts.push(histStr);
  if (data.historyOfSmoking) parts.push('History of smoking');
  if (data.familyHistory) parts.push('Family history of AAA');
  if (data.hypertension) parts.push('Hypertension');
  const reason = parts.length === 0 ? 'Unspecified' : parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('. ');

  const aorta = data.aorta || {};
  const iliac = data.iliacVessels || {};

  const isProximalAbnormal = aorta.proximal && parseFloat(aorta.proximal) >= 3.0;
  const isMidAbnormal = aorta.mid && parseFloat(aorta.mid) >= 3.0;
  const isDistalAbnormal = aorta.distal && parseFloat(aorta.distal) >= 3.0;

  const measurementsText = [
    aorta.proximal ? `Proximal aorta: ${aorta.proximal} cm` : null,
    aorta.mid ? `Mid aorta: ${aorta.mid} cm` : null,
    aorta.distal ? `Distal aorta: ${aorta.distal} cm` : null,
    aorta.peakSystolicVelocity ? `PSV: ${aorta.peakSystolicVelocity} cm/s` : null,
    iliac.rightIliac ? `Right iliac: ${iliac.rightIliac} cm` : null,
    iliac.leftIliac ? `Left iliac: ${iliac.leftIliac} cm` : null,
  ].filter(Boolean).join('\n');

  return (
    <Document>
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="AORTA ULTRASOUND WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <PdfMeasurementBox title="MEASUREMENTS">
              <Text style={commonStyles.infoBoxText}>{measurementsText || 'None recorded'}</Text>
            </PdfMeasurementBox>
            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            <PdfAccentSectionHeader title="AORTA" />
            <PdfLabelValue label="Plaque:" value={aorta.plaque || 'None'} isAbnormal={aorta.plaque && aorta.plaque !== 'None'} />
            <PdfLabelValue label="Proximal diameter:" value={aorta.proximal ? `${aorta.proximal} cm` : ''} isAbnormal={isProximalAbnormal} />
            <PdfLabelValue label="Mid diameter:" value={aorta.mid ? `${aorta.mid} cm` : ''} isAbnormal={isMidAbnormal} />
            <PdfLabelValue label="Distal diameter:" value={aorta.distal ? `${aorta.distal} cm` : ''} isAbnormal={isDistalAbnormal} />
            <PdfLabelValue label="Peak systolic velocity:" value={aorta.peakSystolicVelocity ? `${aorta.peakSystolicVelocity} cm/s` : ''} forceBlack />
            <PdfLabelValue label="Waveform:" value={aorta.waveform || ''} isAbnormal={aorta.waveform && aorta.waveform !== 'Triphasic/Biphasic'} />

            <PdfAccentSectionHeader title="ILIAC VESSELS" />
            <PdfLabelValue label="Right iliac:" value={iliac.rightIliac ? `${iliac.rightIliac} cm` : ''} isAbnormal={iliac.rightIliac && parseFloat(iliac.rightIliac) >= 3.0} />
            <PdfLabelValue label="Left iliac:" value={iliac.leftIliac ? `${iliac.leftIliac} cm` : ''} isAbnormal={iliac.leftIliac && parseFloat(iliac.leftIliac) >= 3.0} />

            <PdfAccentSectionHeader title="RISK FACTORS" />
            <PdfLabelValue label="History of smoking:" value={data.historyOfSmoking ? 'Yes' : 'No'} isAbnormal={data.historyOfSmoking} />
            <PdfLabelValue label="Family history of AAA:" value={data.familyHistory ? 'Yes' : 'No'} isAbnormal={data.familyHistory} />
            <PdfLabelValue label="Hypertension:" value={data.hypertension ? 'Yes' : 'No'} isAbnormal={data.hypertension} />
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>
    </Document>
  );
}
